import {
    ArrowLeftRight,
    Ban,
    CreditCard,
    Fingerprint,
    Gift,
    Package,
    PackageOpen,
    Share2,
    ShieldCheck,
    Truck,
    UserRound,
    Wallet,
    type LucideIcon
} from 'lucide-react'

import { UserPermission } from '@/types/user'

/**
 * The tab order is the order an investigation usually runs in: who they are, what
 * they can do, what they hold, then what they did. The route validates `?tab=`
 * against these ids, so this list is also the URL contract.
 *
 * `permission` is what the tab's own endpoints check, not what the page checks.
 * Opening the record already needs `USER_VIEW`, and most tabs need nothing beyond
 * it; the four that do are named here so a tab whose every request would 403 is
 * never rendered. Verified against the backend routes each tab calls:
 * `POST /secure/admin/wallet` (WALLET_VIEW), `POST /secure/admin/inventory`
 * (INVENTORY_MANAGE), `POST /secure/admin/user/update-permissions`
 * (USER_MANAGE_PERMISSIONS), `POST /secure/admin/payments/withdrawals`
 * (TRANSACTIONS_MANAGE).
 */
export const USER_TABS = [
    { id: 'profile', label: 'Profile', icon: UserRound },
    { id: 'permissions', label: 'Permissions', icon: ShieldCheck, permission: UserPermission.USER_MANAGE_PERMISSIONS },
    { id: 'wallet', label: 'Wallet', icon: Wallet, permission: UserPermission.WALLET_VIEW },
    { id: 'inventory', label: 'Inventory', icon: Package, permission: UserPermission.INVENTORY_MANAGE },
    { id: 'orders', label: 'Orders', icon: Truck, permission: UserPermission.TRANSACTIONS_MANAGE },
    { id: 'transactions', label: 'Transactions', icon: ArrowLeftRight },
    { id: 'timeline', label: 'Timeline', icon: Share2 },
    { id: 'payments', label: 'Payments', icon: CreditCard },
    { id: 'openings', label: 'Card openings', icon: Gift },
    { id: 'packs', label: 'Pack openings', icon: PackageOpen },
    { id: 'logins', label: 'Logins', icon: Fingerprint },
    { id: 'bans', label: 'Ban history', icon: Ban }
] as const satisfies readonly { id: string; label: string; icon: LucideIcon; permission?: UserPermission }[]

export type UserTab = (typeof USER_TABS)[number]
export type UserTabId = UserTab['id']

/**
 * Read as a helper rather than as `tab.permission` because the entries without one
 * genuinely do not carry the key, and narrowing the union on every call site would
 * put the same `in` check in four places.
 */
export function tabPermission(tab: UserTab): UserPermission | undefined {
    return 'permission' in tab ? tab.permission : undefined
}
